import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pagination } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { HiScale } from 'react-icons/hi2';
import { PageContent, PageHeader } from '@/components/layout/PageLayout';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { DataTable, type DataTableColumn } from '@/components/common/DataTable';
import {
  EmptyState, EmptyStateIcon, EmptyStateTitle, EmptyStateDescription,
  DesktopOnly, MobileOnly, MobileCardList, MobileCard, MobileCardHeader, MobileCardBody, MobileCardMain, MobileCardTitle,
  TextMutedSmall,
} from '@/styles/shared.styled';
import { useListPelnomocnicyQuery } from '@/store/pelnomocnicyApi';
import { DirectoryStatusBadge } from '@/components/pelnomocnicy/DirectoryStatusBadge';
import type { PelnomocnikDto } from '@/types';

const PAGE_SIZE = 20;

export default function PelnomocnicyPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useListPelnomocnicyQuery({ page, pageSize: PAGE_SIZE });

  const totalPages = data ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  const columns: DataTableColumn<PelnomocnikDto>[] = [
    { key: 'name', header: t('pelnomocnicy.table.name'), render: (p) => <strong>{p.name}</strong> },
    {
      key: 'type',
      header: t('pelnomocnicy.table.type'),
      render: (p) => p.directoryProfessionalType ? t(`pelnomocnicy.directory.${p.directoryProfessionalType}`) : '—',
    },
    {
      key: 'status',
      header: t('pelnomocnicy.table.status'),
      render: (p) => (
        <DirectoryStatusBadge
          status={p.directoryStatus}
          professionalType={p.directoryProfessionalType}
        />
      ),
    },
    { key: 'address', header: t('pelnomocnicy.table.address'), render: (p) => p.address ?? '—' },
    { key: 'sprawyCount', header: t('pelnomocnicy.table.sprawyCount'), render: (p) => p.sprawyCount, align: 'right' },
  ];

  return (
    <PageContent>
      <PageHeader title={t('pelnomocnicy.title')} subtitle={t('pelnomocnicy.subtitle')} />

      {isLoading ? (
        <LoadingSpinner />
      ) : !data?.items.length ? (
        <EmptyState>
          <EmptyStateIcon><HiScale /></EmptyStateIcon>
          <EmptyStateTitle>{t('pelnomocnicy.empty')}</EmptyStateTitle>
          <EmptyStateDescription>{t('pelnomocnicy.empty')}</EmptyStateDescription>
        </EmptyState>
      ) : (
        <>
          <DesktopOnly>
            <DataTable<PelnomocnikDto>
              ariaLabel={t('pelnomocnicy.title')}
              rowKey={(p) => p.id}
              onRowClick={(p) => navigate(`/pelnomocnicy/${p.id}`)}
              columns={columns}
              rows={data.items}
            />
          </DesktopOnly>

          <MobileOnly>
            <MobileCardList>
              {data.items.map((p) => (
                <MobileCard key={p.id} $clickable onClick={() => navigate(`/pelnomocnicy/${p.id}`)}>
                  <MobileCardHeader>
                    <MobileCardMain>
                      <div className="d-flex align-items-center gap-2">
                        <MobileCardTitle as="span">{p.name}</MobileCardTitle>
                        <DirectoryStatusBadge
                          status={p.directoryStatus}
                          professionalType={p.directoryProfessionalType}
                        />
                      </div>
                      {p.directoryProfessionalType && (
                        <TextMutedSmall>
                          {t(`pelnomocnicy.directory.${p.directoryProfessionalType}`)}
                        </TextMutedSmall>
                      )}
                      {p.address && <TextMutedSmall>{p.address}</TextMutedSmall>}
                    </MobileCardMain>
                  </MobileCardHeader>
                  <MobileCardBody>
                    <span>{p.sprawyCount} {t('pelnomocnicy.table.sprawyCount')}</span>
                  </MobileCardBody>
                </MobileCard>
              ))}
            </MobileCardList>
          </MobileOnly>

          {totalPages > 1 && (
            <div className="d-flex justify-content-end mt-3">
              <Pagination className="mb-0">
                <Pagination.Prev disabled={page <= 1} onClick={() => setPage(page - 1)} />
                {Array.from({ length: totalPages }, (_, i) => (
                  <Pagination.Item key={i + 1} active={page === i + 1} onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </Pagination.Item>
                ))}
                <Pagination.Next disabled={page >= totalPages} onClick={() => setPage(page + 1)} />
              </Pagination>
            </div>
          )}
        </>
      )}
    </PageContent>
  );
}
